// Shared definition of the canonical Trades-grid columns (MPOLISH).
//
// Both the full Trades page (apps/web/src/pages/Trades.tsx) and the
// dashboard Trades widget render the same fixed column set in the same
// order so "all trades columns" stay consistent. The header row differs
// between the two surfaces (the Trades page makes Date an interactive
// sort toggle; the widget renders it static), so this module exports the
// column metadata and the shared row-cell renderer but leaves the
// <TableHead> Date cell to each caller.

import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";

import type { components } from "../../api/schema";
import { formatDate, formatTime } from "../../lib/date";
import { formatMoney, formatPrice } from "../../lib/money";
import {
  formatHoldDuration,
  formatPercent,
  formatQuantity,
  formatRMultiple,
} from "../../lib/tradeFormat";
import { DirectionBadge, PnlGaugeBar, SymbolAvatar, TagPills, TradeNoteBadge } from "./tradeCells";
import { TableCell, TableHead } from "../ui/table";

export type TradeRow = components["schemas"]["TradeSummaryResponse"];

export interface FixedColumn {
  label: string;
  // Header rendered muted (text-on-surface-variant) vs strong
  // (text-on-surface), matching the trades-grid spec.
  muted: boolean;
  // Right-aligned numeric column.
  numeric: boolean;
}

// Order + styling matches the trades-grid spec. "Date / Time" is index 0
// (the opened date + time merged into one column); the Trades page replaces
// its header with a sort button while reusing every other header verbatim.
export const FIXED_TRADE_COLUMNS: readonly FixedColumn[] = [
  { label: "Date / Time", muted: false, numeric: false },
  { label: "Symbol", muted: false, numeric: false },
  { label: "Volume", muted: false, numeric: true },
  { label: "Executions", muted: true, numeric: true },
  { label: "P&L", muted: false, numeric: true },
  { label: "P&L (%)", muted: true, numeric: true },
  { label: "Entry → Exit", muted: false, numeric: false },
  { label: "Tags", muted: true, numeric: false },
];

export const FIXED_TRADE_COLUMN_COUNT = FIXED_TRADE_COLUMNS.length;

function headClassName(column: FixedColumn): string {
  return [column.numeric ? "text-right" : "", column.muted ? "text-on-surface-variant" : ""]
    .filter(Boolean)
    .join(" ");
}

// Render a single fixed-column header cell. `omitFirst` skips the Date
// header so a caller can supply its own (e.g. a sort toggle).
export function FixedTradeHeadCells({ omitFirst = false }: { omitFirst?: boolean }): ReactNode {
  const columns = omitFirst ? FIXED_TRADE_COLUMNS.slice(1) : FIXED_TRADE_COLUMNS;
  return (
    <>
      {columns.map((column) => (
        <TableHead key={column.label} className={headClassName(column)}>
          {column.label}
        </TableHead>
      ))}
    </>
  );
}

export function pnlClassName(value: string | null | undefined): string {
  if (value === null || value === undefined) {
    return "";
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric === 0) {
    return "";
  }
  return numeric > 0 ? "text-success" : "text-danger";
}

export function moneyOrDash(value: string | null | undefined, currency: string): string {
  if (value === null || value === undefined) {
    return "—";
  }
  return formatMoney(value, { currency });
}

export function priceOrDash(value: string | null | undefined, currency: string): string {
  if (value === null || value === undefined) {
    return "—";
  }
  return formatPrice(value, { currency });
}

// The merged Entry → Exit price pair shown in the trades grid (e.g.
// "$1.75 → $1.88"). Shared by the flat row, the grouped roll-up header, and
// any other surface that renders the canonical column set. When both prices
// are absent (an open trade with no projection yet) the cell collapses to a
// single em-dash rather than rendering "— → —".
export function EntryExitCellContent({
  entry,
  exit,
  currency,
}: {
  entry: string | null | undefined;
  exit: string | null | undefined;
  currency: string;
}): ReactNode {
  if (
    (entry === null || entry === undefined) &&
    (exit === null || exit === undefined)
  ) {
    return "—";
  }
  return (
    <span className="inline-flex items-center gap-1 tabular-nums">
      <span>{priceOrDash(entry, currency)}</span>
      <span aria-hidden="true" className="text-on-surface-variant">
        →
      </span>
      <span>{priceOrDash(exit, currency)}</span>
    </span>
  );
}

// The shared data cells for one trade (Date / Time through Tags). The
// merged Date / Time cell is the row's primary navigation into the trade
// detail: the opened date links, with the opened time shown muted beside
// it. Open trades carry null projection fields and render the em-dash.
//
// `nested` renders the row as a constituent of an expanded roll-up: the
// repeated group date is dropped (it reads as a child of the header above),
// the cell indents, and the opened time itself becomes the drill-in link.
export function FixedTradeCells({
  trade,
  nested = false,
  gaugeMax,
  interactiveRow = false,
}: {
  trade: TradeRow;
  nested?: boolean;
  // When provided (> 0), the Net P&L cell shows a diverging magnitude bar
  // scaled to this value (the rendered page's largest |P&L|).
  gaugeMax?: number;
  // When the parent row is itself clickable (flat grid), render Date / Time as
  // plain text in the dashboard scheme (primary date + muted mono time) instead
  // of a nested link, so the whole row opens the trade.
  interactiveRow?: boolean;
}): ReactNode {
  const detailLink = (label: string) => (
    <Link
      to="/trades/$tradeId"
      params={{ tradeId: trade.trade_id }}
      search={{ from: "trades" }}
      className="text-primary"
    >
      {label}
    </Link>
  );
  return (
    <>
      <TableCell className={nested ? "pl-8" : undefined}>
        {interactiveRow ? (
          <>
            <span className="text-primary">{formatDate(trade.opened_at)}</span>
            <span className="ml-2 font-data text-on-surface-muted">
              {formatTime(trade.opened_at)}
            </span>
          </>
        ) : nested ? (
          detailLink(formatTime(trade.opened_at))
        ) : (
          <>
            {detailLink(formatDate(trade.opened_at))}
            <span className="ml-2 text-on-surface-variant">{formatTime(trade.opened_at)}</span>
          </>
        )}
      </TableCell>
      <TableCell className="font-medium text-on-surface">
        <span className="flex items-center gap-2">
          <SymbolAvatar symbol={trade.primary_symbol} size="sm" />
          <span>{trade.primary_symbol ?? "—"}</span>
          <DirectionBadge direction={trade.primary_direction} />
          <TradeNoteBadge count={trade.notes_count} />
        </span>
      </TableCell>
      <TableCell className="text-right tabular-nums">{formatQuantity(trade.quantity_total)}</TableCell>
      <TableCell className="text-right tabular-nums text-on-surface-variant">
        {trade.execution_count ?? "—"}
      </TableCell>
      <TableCell className={`text-right tabular-nums ${pnlClassName(trade.realized_pnl_amount)}`}>
        <span className="inline-flex items-center justify-end gap-2">
          {gaugeMax !== undefined && gaugeMax > 0 ? (
            <span className="hidden w-16 sm:inline-block">
              <PnlGaugeBar value={trade.realized_pnl_amount} max={gaugeMax} />
            </span>
          ) : null}
          {moneyOrDash(trade.realized_pnl_amount, trade.currency)}
        </span>
      </TableCell>
      <TableCell className={`text-right tabular-nums ${pnlClassName(trade.realized_return_pct)}`}>
        {formatPercent(trade.realized_return_pct)}
      </TableCell>
      <TableCell>
        <EntryExitCellContent
          entry={trade.average_entry_price}
          exit={trade.average_exit_price}
          currency={trade.currency}
        />
      </TableCell>
      <TableCell>
        <TagPills csv={trade.tag_labels_csv} />
      </TableCell>
    </>
  );
}

// --- Optional (opt-in) columns -------------------------------------------
//
// Columns dropped from the default trades-grid layout but available as
// toggles via the Columns sidebar. Shared by the flat Trades grid and the
// scalper roll-up grid so both honor the same visibility set.

export type OptionalColumn =
  | "status"
  | "leg_count"
  | "planned_risk"
  | "hold_duration"
  | "r_multiple"
  | "strategy"
  | "notes";

export const OPTIONAL_COLUMN_LABELS: Record<OptionalColumn, string> = {
  status: "Status",
  leg_count: "Legs",
  planned_risk: "Planned risk",
  hold_duration: "Hold time",
  r_multiple: "R",
  strategy: "Strategy",
  notes: "Notes",
};

export const OPTIONAL_COLUMN_ORDER: readonly OptionalColumn[] = [
  "status",
  "leg_count",
  "planned_risk",
  "hold_duration",
  "r_multiple",
  "strategy",
  "notes",
];

export function formatMoneyOrDash(value: string | null | undefined, currency: string): string {
  return moneyOrDash(value, currency);
}

// Header <TableHead> cells for the currently-visible optional columns.
export function OptionalHeadCells({
  columns,
}: {
  columns: readonly OptionalColumn[];
}): ReactNode {
  return (
    <>
      {columns.map((key) => (
        <TableHead key={key}>{OPTIONAL_COLUMN_LABELS[key]}</TableHead>
      ))}
    </>
  );
}

// One optional data cell's contents for a given trade.
export function renderOptionalCell(key: OptionalColumn, trade: TradeRow): ReactNode {
  switch (key) {
    case "status":
      return trade.status;
    case "leg_count":
      return trade.leg_count;
    case "planned_risk":
      return moneyOrDash(trade.planned_risk_amount, trade.currency);
    case "hold_duration":
      return formatHoldDuration(trade.hold_duration_seconds);
    case "r_multiple":
      return formatRMultiple(trade.r_multiple);
    case "strategy":
      return <span className="font-mono text-xs">{trade.strategy_id ?? "—"}</span>;
    case "notes":
      return (
        <span className="max-w-xs truncate">{trade.notes.length === 0 ? "—" : trade.notes}</span>
      );
  }
}
